import { useCallback, useMemo, useState } from "react"
import { GetApp } from "../../App"
import { GetDay, GetShip, GetShipContext } from "../../App/Context"
import { useEditState } from "../../App/useEditState"
import { AppViewData } from "../App/Context"
import { Block, BlockField, BlockFieldInputs, BlockRow } from "../../Components/Block"
import { EditGroup, EditGroupData } from "../../Components/EditGroup"
import { Icon, Icons } from "../../Components/Icon"
import { IDay, IDayWatchData, IDayWatchGroup, IDayWatchShift, IDayWatchType, IDayResponse, IDayUpdateRequest, IShipUserCrewRole, Day, ApiClient, DayApi } from "../../Types"
import { ShipUserSelect } from "../../Components/ShipUserSelect"
import { Select, SelectOption } from "../../Components/Select"
import { logger, fieldNames } from "../../App/Logger"
import { newUUID } from "../../App/Identifier"

export const DayWatchViewData = {
  saveDay(api: ApiClient, shipId: number, dayId: number, values: IDayUpdateRequest): Promise<IDayResponse> {
    return new DayApi(api).days.update({ params: { shipId, dayId }, body: values }).call();
  },
};

export interface DayWatchViewProps {
  day: IDay;
}

export function DayWatchView({ day }: DayWatchViewProps) {
  const app = GetApp();
  const { apiUser } = GetShipContext();
  const ship = GetShip();
  const { isCurrentTrip, updateDay } = GetDay();
  const editable = app.editable && AppViewData.availableCurrentOrAll(apiUser, ship.id, isCurrentTrip);
  const t = app.getT("DayWatchView");
  const [edited, onEdited, resetEdited] = useEditState();

  const initialWatchData = useMemo((): IDayWatchData => {
    const data = Day.parse(day).dataParsed;
    if (data.watch) {
      return data.watch;
    }
    return { type: IDayWatchType.manual, groups: [], shifts: [] };
  }, [day]);
  const [watchData, setWatchData] = useState<IDayWatchData>(initialWatchData);
  const [watchGroups, setWatchGroups] = useState<IDayWatchGroup[]>(initialWatchData.groups || []);
  const [watchShifts, setWatchShifts] = useState<IDayWatchShift[]>(initialWatchData.shifts || []);

  const editableFields = [{ name: "watch", type: "object", fields: [
    { name: "type", type: "string" },
    { name: "groups", type: "object", array: true, fields: ["id", "name", { name: "people", array: true, type: "int" }] },
    { name: "shifts", type: "object", array: true, fields: ["id", { name: "start", type: "int" }, { name: "duration", type: "int" }, "group"] },
  ] }];

  const newId = newUUID;

  const save = useCallback(async (_data: FormData, values: EditGroupData) => {
    logger.debug("save", fieldNames(values));
    const result = await updateDay({ data: JSON.stringify(values) });
    if (result) {
      const day = result.day;
      const data = Day.parse(day).dataParsed;
      const watch = data.watch;
      if (watch) {
        setWatchData(watch);
        setWatchGroups(watch.groups || []);
        setWatchShifts(watch.shifts || []);
      }
      resetEdited();
    }
  }, [updateDay, resetEdited]);

  const addMoreWatchShifts = useMemo(() => {
    if (watchShifts.length < 1) {
      return true;
    }
    const lastShift = watchShifts[watchShifts.length - 1];
    if ((lastShift.start + lastShift.duration) < 24) {
      return true;
    }
    return false;
  }, [watchShifts]);

  const nextWatchShift = useCallback((): IDayWatchShift => {
    const lastShift = watchShifts.length > 0 ? watchShifts[watchShifts.length - 1] : undefined;
    if (lastShift) {
      return { id: newId(), start: (lastShift.start + lastShift.duration), duration: lastShift.duration, group: undefined };
    }
    return { id: newId(), start: 0, duration: 4, group: undefined };
  }, [watchShifts]);

  return (
    <div className="DayWatchView">
      <EditGroup changed={edited} editable={editable} editableFields={editableFields} onSave={save}>
        <Block>
          <BlockRow><h3>{t("groups_title")}</h3></BlockRow>
          <input type="hidden" name="watch.type" value={watchData.type} />
          {watchGroups.map((group, index) => {
            return <WatchGroupView key={index} crewIds={day.crew || []} editable={editable} group={group} index={index} onEdited={onEdited} showLabels={index === 0} />;
          })}
          <BlockRow>
            <BlockFieldInputs>
              <button className="button" disabled={!editable} onClick={() => { setWatchGroups([...watchGroups, { id: newId(), name: "", people: [] }]); onEdited(); }}>
                <Icon icon={Icons.Plus} /> {t("add_group")}
              </button>
            </BlockFieldInputs>
          </BlockRow>

          <BlockRow><h3>{t("shifts_title")}</h3></BlockRow>
          <input type="hidden" name="watch.shifts.length" value={watchShifts.length} />
          {watchShifts.map((shift, index) => {
            return <WatchShiftsView key={index} editable={editable} shift={shift} index={index} onEdited={onEdited} showLabels={index === 0} watchGroups={watchGroups} />;
          })}
          <BlockRow>
            <BlockFieldInputs>
              <button className="button" disabled={!editable || !addMoreWatchShifts} onClick={() => { setWatchShifts([...watchShifts, nextWatchShift()]); onEdited(); }}>
                <Icon icon={Icons.Plus} /> {t("add_shift")}
              </button>
            </BlockFieldInputs>
            <BlockField>
              <button className="button" disabled={!editable || watchShifts.length < 1} onClick={() => { setWatchShifts([]); onEdited(); }}>
                <Icon icon={Icons.Plus} /> {t("reset_shifts")}
              </button>
            </BlockField>
          </BlockRow>
        </Block>
      </EditGroup>
    </div>
  );
}

interface WatchGroupViewProps {
  crewIds: number[];
  editable: boolean;
  group: IDayWatchGroup;
  index: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  onEdited?: any;
  showLabels?: boolean;
}

export function WatchGroupView({ crewIds, editable, group, index, onEdited, showLabels = false }: WatchGroupViewProps) {
  const app = GetApp();
  const t = app.getT("DayWatchView_groups");
  const namePrefix = `watch.groups[${index}].`;

  return (
    <BlockRow className="DayWatchView-group">
      <input type="hidden" name={`${namePrefix}id`} defaultValue={group.id} />
      <BlockField label={showLabels ? t("name") : undefined} className="block_field_small_12">
        <BlockFieldInputs>
          <input type="text" defaultValue={group.name} name={`${namePrefix}name`} onChange={onEdited} readOnly={!editable} />
        </BlockFieldInputs>
      </BlockField>
      <BlockField label={showLabels ? t("people") : undefined}>
        <ShipUserSelect defaultValue={group.people} multi={true} onChange={onEdited} restrictIds={crewIds} role={IShipUserCrewRole.crew} name={`${namePrefix}people`} readOnly={!editable} />
      </BlockField>
    </BlockRow>
  );
}

interface WatchShiftsViewProps {
  editable: boolean;
  shift: IDayWatchShift;
  index: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  onEdited?: any;
  showLabels?: boolean;
  watchGroups: IDayWatchGroup[];
}

export function WatchShiftsView({ editable, shift, index, onEdited, showLabels = false, watchGroups }: WatchShiftsViewProps) {
  const app = GetApp();
  const t = app.getT("DayWatchView_shifts");
  const namePrefix = `watch.shifts[${index}].`;
  const [watchGroup, setWatchGroup] = useState<string | undefined>(shift.group || undefined);

  // Create options for the Select component
  const watchGroupOptions = useMemo(() => {
    const options: SelectOption[] = watchGroups.map((group): SelectOption => {
      return {
        title: group.name,
        value: group.name,
      };
    });
    return options;
  }, [watchGroups]);
  const selectWatchGroup = useCallback((value?: string) => {
    setWatchGroup(value);
    if (onEdited) {
      onEdited();
    }
  }, [onEdited]);

  return (
    <BlockRow className="DayShiftsView-group">
      <input type="hidden" name={`${namePrefix}id`} defaultValue={shift.id} />
      <BlockField label={showLabels ? t("hour") : undefined} className="block_field_small_4">
        <BlockFieldInputs>
          <input defaultValue={shift.start} name={`${namePrefix}start`} onChange={onEdited} readOnly={!editable} className="center" />
        </BlockFieldInputs>
      </BlockField>

      <BlockField label={showLabels ? t("duration") : undefined} className="block_field_small_8">
        <BlockFieldInputs>
          <input defaultValue={shift.duration} name={`${namePrefix}duration`} onChange={onEdited} readOnly={!editable} className="center" />
        </BlockFieldInputs>
      </BlockField>

      <BlockField label={showLabels ? t("group") : undefined} className="block_field_small_12">
        <Select button={(watchGroup) ? watchGroup : t(`group_${watchGroup}`)} name={`${namePrefix}group`} value={watchGroup} onSelect={selectWatchGroup} options={watchGroupOptions} readOnly={!editable}></Select>
      </BlockField>

    </BlockRow>
  );
}
